import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Fetch from "../../../services/Fetch";
import { useConstants } from "../../../hooks/UseConstants";
import { useTableStyles } from "../../../hooks/UseTableStyles";
import { usePopups } from "../../../hooks/UsePopups";
import { useWaits } from "../../../hooks/UseWait";
import DeleteDialog from "../../../popup/DeleteDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { usePagination } from "../../../hooks/UsePagination";
import { useSearch } from "../../../hooks/UseSearch";
import ModalOverlay from "../../../components/ModalOverlay";

function MajorsTab({ setSnackBar }) {
    const intl = useIntl();
    const theme = useTheme();
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch } = useSearch();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const [items, setItems] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [total, setTotal] = useState(0);
    const [item, setItem] = useState(null);
    const [deleteId, setDeleteId] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [nameAr, setNameAr] = useState('');
    const [level, setLevel] = useState('university');
    const [universityId, setUniversityId] = useState('');

    const fetchUniversities = async () => {
        let all = [];
        let p = 1;
        let last = 1;
        do {
            const result = await Fetch(host + `/universities?page=${p}&per_page=50`, 'GET', null);
            if (result.status !== 200) break;
            all = [...all, ...(result.data.data || [])];
            last = result.data.pagination?.last_page || 1;
            p++;
        } while (p <= last);
        setUniversities(all);
        if (all.length && !universityId) setUniversityId(String(all[0].id));
    };

    const fetchList = async () => {
        const result = await Fetch(host + `/majors?page=${page + 1}&search=${encodeURIComponent(search)}`, 'GET', null);
        if (result.status === 200) {
            setItems(result.data.data || []);
            setTotal(result.data.pagination?.total ?? 0);
            setTotalPages(result.data.pagination?.last_page || 1);
            setCurrentPage(page);
        }
        setGetWait(false);
    };

    const resetForm = () => {
        setNameEn('');
        setNameAr('');
        setLevel('university');
        setUniversityId(universities[0] ? String(universities[0].id) : '');
        setItem(null);
    };

    const save = async (isUpdate) => {
        setSendWait(true);
        const formData = new FormData();
        formData.append('name_en', nameEn);
        formData.append('name_ar', nameAr);
        formData.append('level', level);
        formData.append('university_id', universityId);
        const url = isUpdate ? `${host}/admin/majors/${item.id}/update` : `${host}/admin/majors/store`;
        const result = await Fetch(url, 'POST', formData);
        if (result.status === 200) {
            setSnackBar('success', <FormattedMessage id={isUpdate ? 'major_updated_msg' : 'major_added_msg'} />);
            await fetchList();
            resetForm();
            setPopup(isUpdate ? 'majors_update' : 'majors_add', 'none');
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors?.[0] || result.data.message);
        }
        setSendWait(false);
    };

    const remove = async () => {
        const result = await Fetch(host + `/admin/majors/${deleteId}/delete`, 'DELETE', null);
        if (result.status === 200) {
            setItems((list) => list.filter((i) => i.id !== deleteId));
            setSnackBar('success', <FormattedMessage id="major_deleted_msg" />);
            setPopup('majors_delete', 'none');
        }
    };

    useEffect(() => {
        fetchUniversities();
    }, []);

    useEffect(() => {
        setGetWait(true);
        fetchList();
    }, [page, search]);

    const levelLabel = (lv) => lv === 'school' ? intl.formatMessage({ id: 'school_education' }) : intl.formatMessage({ id: 'university_education' });

    const FormPopup = ({ id, titleId, onSave }) => (
        <ModalOverlay id={id} onClose={() => { resetForm(); setPopup(id, 'none'); }}>
            <Box sx={{ backgroundColor: theme.palette.background.paper }} className="shadow-lg w-2/5 min-w-[320px] rounded-3xl px-4 py-5 relative" dir={language === 'en' ? 'ltr' : 'rtl'}>
                <Typography variant="h6"><FormattedMessage id={titleId} /></Typography>
                <CloseIcon onClick={() => { resetForm(); setPopup(id, 'none'); }} className="cursor-pointer absolute top-5 left-5" sx={{ left: language === 'en' && '90%' }} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_ar" />} className="w-full !mt-5" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_en" />} className="w-full !mt-3" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                <Typography variant="body2" className="text-gray-500 mt-4 mb-1"><FormattedMessage id="education_stage" /></Typography>
                <Select fullWidth value={level} onChange={(e) => setLevel(e.target.value)}>
                    <MenuItem value="school"><FormattedMessage id="school_education" /></MenuItem>
                    <MenuItem value="university"><FormattedMessage id="university_education" /></MenuItem>
                </Select>
                <Typography variant="body2" className="text-gray-500 mt-4 mb-1"><FormattedMessage id="universities" /></Typography>
                <Select fullWidth value={universityId} onChange={(e) => setUniversityId(e.target.value)} disabled={!universities.length}>
                    {universities.map((u) => (
                        <MenuItem key={u.id} value={String(u.id)}>{language === 'en' ? u.name_en : u.name_ar}</MenuItem>
                    ))}
                </Select>
                {!universities.length && (
                    <Typography variant="caption" className="text-red-500 block mt-2"><FormattedMessage id="add_university_first" /></Typography>
                )}
                <Box className="flex gap-2 mt-5">
                    <Button variant="contained" onClick={onSave} disabled={sendWait || !universities.length}><FormattedMessage id="save" /></Button>
                    <Button variant="contained" className="!bg-gray-400" onClick={() => setPopup(id, 'none')}><FormattedMessage id="cancel" /></Button>
                </Box>
            </Box>
        </ModalOverlay>
    );

    return (
        <>
            {getWait ? <Box className="flex justify-center py-20"><CircularProgress size={50} /></Box> : (
                <Box sx={{ backgroundColor: theme.palette.background.paper }} className="rounded-xl px-2">
                    <Box className="flex justify-between items-center px-2 py-2">
                        <Typography variant="h6"><FormattedMessage id="majors" /></Typography>
                        <Button variant="contained" onClick={() => { resetForm(); setPopup('majors_add', 'flex'); }} disabled={!universities.length}><AddIcon /><FormattedMessage id="add_major" /></Button>
                    </Box>
                    <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : 'rtl'}>
                        <Box className="py-2 px-2 flex justify-between">
                            <Box className="w-2/4 relative">
                                <input style={{ backgroundColor: theme.palette.background.default }} onChange={(e) => { setPage(0); setSearch(e.target.value); }} className="w-full h-12 rounded-md border indent-14 outline-none" placeholder={intl.formatMessage({ id: 'search_majors' })} />
                                <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" />
                            </Box>
                            <Typography variant="body2" className="text-gray-500"><FormattedMessage id="total_majors" />: {total}</Typography>
                        </Box>
                        <Table>
                            <TableHead><TableRow>
                                <StyledTableCell><FormattedMessage id="category_name" /></StyledTableCell>
                                <StyledTableCell><FormattedMessage id="education_stage" /></StyledTableCell>
                                <StyledTableCell><FormattedMessage id="universities" /></StyledTableCell>
                                <StyledTableCell align="center"><FormattedMessage id="procedures" /></StyledTableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {items.map((row) => (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell>{language === 'en' ? row.name_en : row.name_ar}</StyledTableCell>
                                        <StyledTableCell>{levelLabel(row.level)}</StyledTableCell>
                                        <StyledTableCell>{row.university ? (language === 'en' ? row.university.name_en : row.university.name_ar) : '—'}</StyledTableCell>
                                        <StyledTableCell align="center" className="!flex justify-center gap-2">
                                            <Button size="small" variant="contained" className="!bg-green-300 !text-green-800" onClick={() => {
                                                setItem(row);
                                                setNameEn(row.name_en);
                                                setNameAr(row.name_ar);
                                                setLevel(row.level || 'university');
                                                setUniversityId(String(row.university?.id || row.university_id || ''));
                                                setPopup('majors_update', 'flex');
                                            }}><FormattedMessage id="update" /></Button>
                                            <Button size="small" variant="contained" className="!bg-red-300 !text-red-700" onClick={() => { setDeleteId(row.id); setPopup('majors_delete', 'flex'); }}><FormattedMessage id="delete" /></Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box className="flex justify-center py-3" dir="rtl">
                            <Button disabled={page + 1 >= totalPages} onClick={() => setPage(currentPage + 1)}><NavigateNextIcon /></Button>
                            <Typography dir="ltr">{currentPage + 1} / {totalPages}</Typography>
                            <Button disabled={page === 0} onClick={() => setPage(currentPage - 1)}><NavigateBeforeIcon /></Button>
                        </Box>
                    </TableContainer>
                </Box>
            )}
            <FormPopup id="majors_add" titleId="add_major" onSave={() => save(false)} />
            <FormPopup id="majors_update" titleId="update_major" onSave={() => save(true)} />
            <ModalOverlay id="majors_delete" onClose={() => setPopup('majors_delete', 'none')}>
                <DeleteDialog onClickConfirm={remove} onClickCancel={() => setPopup('majors_delete', 'none')} title={<FormattedMessage id="major_delete" />} subtitle={<FormattedMessage id="major_delete_description" />} />
            </ModalOverlay>
        </>
    );
}

export default MajorsTab;
